import { produce } from 'immer';
import { useEffect, useState } from 'react';
import {
  AnswerTypes,
  getSurvey,
  QuestionModel,
  submitSurvey,
  Survey
} from 'src/api';
import { getQueryParam } from 'src/core';
import { months } from '../../app/pages/survey/utils';

/**
 * Use Form Data
 */
const useFormData = () => {
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState(null);
  const [data, setData] = useState(null);
  const [results, setResults] = useState({} as any);
  const [isQuestionAnswered, setIsQuestionAnswered] = useState(false);
  const [isQuestionTouched, setIsQuestionTouched] = useState(false);
  const [isSingleError, setIsSingleError] = useState(false);
  const [isIndividualSurvey, setIsIndividualSurvey] = useState(false);
  const { questions } = data || ({} as Survey);
  const [validation, setValidation] = useState([]);
  const [clientId, setClientId] = useState('');
  const [mobileConfirmation, setMobileConfirmation] = useState(false);
  const [emailConfirmation, setEmailConfirmation] = useState(false);
  const [evt, setEvt] = useState(false);
  const [geoLocationType, setGeoLocationType] = useState(false);
  const [userPhone, setUserPhone] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [lastPageText, setLastPageText] = useState({
    caption: '',
    description: ''
  });
  const [firstPageText, setFirstPageText] = useState({
    button: '',
    caption: '',
    counter: '',
    description: ''
  });
  const [surveyIsCompleted, setSurveyIsCompleted] = useState(true);
  const [surveyIsNotFound, setSurveyIsNotFound] = useState(false);

  const id = getQueryParam('id');

  const setPhoneNumber = (phone: string) => {
    setUserPhone(phone);
  };
  const setEmail = (email: string) => {
    setUserEmail(email);
  };

  const changeIsSingleError = (value: boolean) => {
    setIsSingleError(value);
  };

  const changeIsQuestionAnswered = (value: boolean) => {
    setIsQuestionAnswered(value);
  };

  const changeIsQuestionTouched = (value: boolean) => {
    setIsQuestionTouched(value);
  };

  const changeQuestionValue = (question: QuestionModel, value: any) => {
    if (!question) return;

    const { questionId: qId, attr_name } = question;

    const questionId = qId;

    if (value)
      setValidation(
        validation?.filter((item: any) => item.questionId !== questionId)
      );

    setResults(
      produce(results, (draft: any) => {
        const questionToChange = questions.find(
          item => item.questionId === questionId
          // || item.attr_name === questionId
        );

        const answerToChange = questionToChange?.answers[0];

        switch (answerToChange?.type) {
          case AnswerTypes.phone:
            if (attr_name) {
              draft[attr_name] = '380' + value.replaceAll(' ', '');
            }

            draft[questionId] = '380' + value.replaceAll(' ', '');

            break;

          case AnswerTypes.text:
          case AnswerTypes.scale:
          case AnswerTypes.dropdown:
          case AnswerTypes.geoLocation:
          case AnswerTypes.inn:
          case AnswerTypes.email_pmi:
          case AnswerTypes.text_small:
          case AnswerTypes.slider:
          case AnswerTypes.radiobutton: {
            changeIsQuestionAnswered(true);

            if (attr_name) {
              draft[attr_name] = value;
            }

            draft[questionId] = value;

            break;
          }

          case AnswerTypes.radiobuttonMultiple:
          case AnswerTypes.dropdownMultiple:
          case AnswerTypes.dropdownMultipleSingle:
          case AnswerTypes.numberMultiple:
          case AnswerTypes.demicalMultiple:
          case AnswerTypes.checkbox:
          case AnswerTypes.checkboxNumberMultiple:
          case AnswerTypes.familiarityScalePlaceRotation:
          case AnswerTypes.familiarityScaleRange: {
            if (attr_name) {
              draft[attr_name] = value;
            }

            draft[questionId] = value;

            break;
          }

          case AnswerTypes.familiarityScaleRangeSingle: {
            if (attr_name) {
              draft[attr_name] = Object.values(value)?.[0];
            }

            draft[questionId] = Object.values(value)?.[0];

            break;
          }

          case AnswerTypes.birthYear: {
            const key = months.includes(value) ? 'month' : 'year';

            if (attr_name) {
              draft[attr_name] = { ...draft[questionId], [key]: value };
            }

            draft[questionId] = { ...draft[questionId], [key]: value };

            break;
          }

          case AnswerTypes.buttons: {
            if (attr_name) {
              draft[attr_name] = { text: value };
            }

            draft[questionId] = { text: value };

            break;
          }

          case AnswerTypes.upload: {
            if (attr_name) {
              draft[attr_name] = value?.Location;
            }

            draft[questionId] = value?.Location;

            break;
          }
        }
      })
    );
  };

  const sendAnswers = () => {
    submitSurvey({
      survey,
      isIndividualSurvey,
      results,
      userPhone,
      userEmail
    });
  };

  /**
   * Get Form values on startup
   */
  useEffect(() => {
    setLoading(true);
    setSurveyIsNotFound(false);

    getSurvey(id as string).then(survey => {
      setLoading(false);

      if (!survey) {
        setSurveyIsNotFound(true);
        return;
      }

      const {
        template: { template: res }
      } = survey;

      setSurvey(survey);
      setIsIndividualSurvey(!!survey.phone);

      res.questions?.questions.forEach((question: any) => {
        if (question.answers[0].type === 'geo_location') {
          setGeoLocationType(true);
        }
      });
      setUserPhone(survey.phone);
      setUserEmail(survey.email);
      setLastPageText(res.finalPageText);
      setFirstPageText(res.startPageText);
      setData((res?.questions as any) || null);
      setClientId(res?.client_id);
      setMobileConfirmation(res?.mobileConfirmation);
      setEmailConfirmation(res?.emailConfirmation);
      setEvt(res?.evt);

      if (!res) return;

      setResults(
        res.questions?.questions?.reduce(
          (results: any, question: any) => ({
            ...results,
            [question?.questionId]: ''
          }),
          {}
        )
      );
      setValidation(
        res.questions?.questions?.filter((item: any) => item.required) as any
      );

      if (!survey.phone) {
        setSurveyIsCompleted(false);

        return;
      }

      setSurveyIsCompleted(survey.completed);
    });
  }, [id]);

  return {
    loading,
    data: (data as unknown) as Survey,
    id,
    changeQuestionValue,
    changeIsQuestionAnswered,
    changeIsQuestionTouched,
    changeIsSingleError,
    results,
    questions,
    setResults,
    validation,
    isQuestionAnswered,
    isQuestionTouched,
    isSingleError,
    clientId,
    mobileConfirmation,
    emailConfirmation,
    evt,
    geoLocationType,
    setPhoneNumber,
    setEmail,
    setSurveyIsCompleted,
    sendAnswers,
    lastPageText,
    firstPageText,
    surveyIsCompleted,
    userPhone,
    survey,
    surveyIsNotFound
  };
};

export { useFormData };
